import { orgAPI } from "@/api/api";
import Sentry from "@/integrations/Sentry";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import {
  extractReferencedFiles,
  PUBLIC_URL_REGEX,
  reversePresignedUrlsIn,
} from "@cohesive/ui-components";
import { useCallback, useMemo, useRef } from "react";

export interface HtmlPresignRewrite {
  /** Rewrite embedded public file URLs to presigned ones before display
   *  (one bulk round trip). Transport errors leave the HTML untouched. */
  transformHtml: (html: string) => Promise<string>;
  /**
   * Reverse of `transformHtml` for content coming BACK from a live iframe
   * (island innerHTML): swap presigned URLs to their stable public form so
   * saves never persist expiring URLs. Handles both the raw URL and its
   * `&`→`&amp;` variant (innerHTML serializes ampersands in attributes).
   */
  reversePresignedUrls: (html: string) => string;
  /**
   * Iframe asset-404 feedback (detail pane): mark the file record behind a
   * presigned URL as missing so it is never re-presigned — its original
   * public URL stays in place on the next rewrite. Returns true when newly
   * marked (caller should re-deliver the content).
   */
  markMissingByPresignedUrl: (url: string) => boolean;
  /** Ids that should no longer be presigned — folded into the pane's srcDoc
   *  fingerprint so a 404-driven re-deliver registers as new content. */
  getKnownMissingIds: () => ReadonlySet<string>;
  /** Clear all bookkeeping (detail pane record switch). */
  reset: () => void;
}

export interface UseHtmlPresignRewriteOptions {
  /** Sentry context for presign failures. */
  fileRecordId?: string;
}

/**
 * ONE presign rewrite for every HTML surface — the detail pane and the
 * canvas block card share this hook (previously two near-identical copies:
 * `rewritePublicFileUrls` in HtmlContent and `useHtmlBlockContentTransform`).
 *
 * Rewrites `https://file.{env}.cohesive.ai/public/{id}` references to
 * short-lived presigned URLs via the server's bulk-presign endpoint, so
 * embedded assets resolve even when unpublished. Per-id failures (record
 * deleted, still processing, permission denied) accumulate in
 * `knownMissingIds` and keep their original public URL — stable and
 * CloudFront-cacheable, and a published copy may still serve them.
 *
 * The presigned→public map ACCUMULATES across rotations (never replaced):
 * a non-remounting iframe can hold island content presigned by an earlier
 * delivery, and reversal must still find it — replacing the map per delivery
 * would let an old presigned URL slip into a save.
 */
export function useHtmlPresignRewrite(
  options: UseHtmlPresignRewriteOptions = {},
): HtmlPresignRewrite {
  const fileRecordIdRef = useRef(options.fileRecordId);
  fileRecordIdRef.current = options.fileRecordId;

  const knownMissingIdsRef = useRef<Set<string>>(new Set());
  // presigned URL → the exact public URL string it replaced.
  const presignedToPublicRef = useRef<Map<string, string>>(new Map());
  // presigned URL → file-record id (asset-404 traceback).
  const presignedToIdRef = useRef<Map<string, string>>(new Map());

  // The public share SPA has no authenticated API: a bulk-presign call there
  // would sit on the axios auth interceptor waiting for a token getter that
  // is never installed. Serve the HTML untouched — embedded /public/{id}
  // URLs stay stable and CloudFront-cacheable, and resolve whenever the
  // referenced file is itself published.
  const isPublicCanvasView = useIsPublicCanvasView();

  const transformHtml = useCallback(
    async (html: string) => {
      if (isPublicCanvasView) return html;
      const referenced = extractReferencedFiles(html);
      const idsToPresign = referenced
        .map((ref) => ref.id)
        .filter((id) => !knownMissingIdsRef.current.has(id));
      if (idsToPresign.length === 0) return html;

      let replacements: Map<string, string>;
      try {
        const items = await orgAPI.fileRecords.bulkPresignURLs(idsToPresign);
        replacements = new Map();
        for (const item of items) {
          if (item.url) {
            replacements.set(item.file_record_id, item.url);
          } else {
            knownMissingIdsRef.current.add(item.file_record_id);
          }
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: "html-publish-rewrite" },
          extra: {
            fileRecordId: fileRecordIdRef.current,
            referencedIdCount: referenced.length,
            stage: "bulk-presign",
          },
        });
        return html;
      }
      if (replacements.size === 0) return html;

      return html.replace(PUBLIC_URL_REGEX, (original, id: string) => {
        const presigned = replacements.get(id);
        if (!presigned) return original;
        presignedToPublicRef.current.set(presigned, original);
        presignedToIdRef.current.set(presigned, id);
        return presigned;
      });
    },
    [isPublicCanvasView],
  );

  const reversePresignedUrls = useCallback(
    (html: string) =>
      reversePresignedUrlsIn(html, presignedToPublicRef.current),
    [],
  );

  const markMissingByPresignedUrl = useCallback((url: string) => {
    const id = presignedToIdRef.current.get(url);
    if (!id || knownMissingIdsRef.current.has(id)) return false;
    knownMissingIdsRef.current.add(id);
    return true;
  }, []);

  const getKnownMissingIds = useCallback(
    (): ReadonlySet<string> => knownMissingIdsRef.current,
    [],
  );

  const reset = useCallback(() => {
    knownMissingIdsRef.current = new Set();
    presignedToPublicRef.current = new Map();
    presignedToIdRef.current = new Map();
  }, []);

  return useMemo(
    () => ({
      transformHtml,
      reversePresignedUrls,
      markMissingByPresignedUrl,
      getKnownMissingIds,
      reset,
    }),
    [
      transformHtml,
      reversePresignedUrls,
      markMissingByPresignedUrl,
      getKnownMissingIds,
      reset,
    ],
  );
}
